import LocalizedLink from "@/components/LocalizedLink";
import SectionHeading from "@/components/SectionHeading";
import ArticleCard from "@/components/ArticleCard";
import NextArrow from "@/components/NextArrow";
import CommunityActions from "@/components/CommunityActions";
import SaveButton from "@/components/SaveButton";
import NewsletterSignup from "@/components/NewsletterSignup";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Users } from "lucide-react";
import { useArticles, useTools } from "@/content/ContentProvider";
import { useLanguage } from "@/i18n/LanguageContext";

/**
 * Home page — redesigned for launch.
 *
 * Goals (from product brief):
 *   - Calm, elegant, institution-ready (not a startup landing page).
 *   - Strong centered hero — one identity, one headline, two CTAs.
 *   - Mission, value, and action communicated within seconds.
 *   - Less clutter: no trust chips, no decorative dial graphic, no
 *     team preview, no duplicated "who we are" block.
 *
 * Final section order:
 *   1. Hero (centered)
 *   2. Vision 2026
 *   3. Featured Tools (3 cards + "View all")
 *   4. Workshops & Programs (3 formats + Browse / Request CTAs)
 *   5. Impact (4 metrics — clean grid, no charts)
 *   6. Articles (2 featured + "View all")
 *   7. Institutional CTA (centered)
 *
 * The footer (with the renamed "Powered by Emergent" link) is rendered
 * by the global Layout, not by this page.
 */

const C = {
  ar: {
    // 1) Hero
    eyebrow: "SAS · مجتمع الذكاء الاصطناعي السوري",
    title: "مجتمع الذكاء الاصطناعي السوري",
    lead:
      "مبادرة تطوعية غير ربحية تهدف إلى نشر الوعي العملي بالذكاء الاصطناعي في سوريا، عبر أدوات مجانية، محتوى عربي مبسّط، وورشات تعليمية تطبيقية للطلاب والباحثين والمجتمع.",
    ctaTools: "استكشف الأدوات",
    ctaWorkshops: "سجّل في الورشات",
    ctaCommunity: "انضم إلى المجتمع",
    ctaCommunityDesc: "تواصل مع الطلاب وتعلم الذكاء الاصطناعي معنا",
    communityCount: "انضم إلى أكثر من 100 طالب يتعلمون الذكاء الاصطناعي",

    // 2) Vision 2026
    visionEyebrow: "رؤيتنا 2026",
    visionTitle: "مسار تعليمي عربي متكامل للذكاء الاصطناعي.",
    visionBody:
      "نطمح إلى توسيع البرنامج داخل الجامعات والمدارس والمنظمات، وبناء مسار تعليمي عربي متكامل يربط بين الوعي والأدوات والتطبيق العملي، لتتحول SAS إلى منصّة تعليمية مجتمعية تدعم الطلاب والباحثين والمهتمين بالذكاء الاصطناعي في سوريا.",
    visionCta: "اقرأ عن الرؤية",

    // 3) Featured tools
    featuredEyebrow: "الأدوات المميّزة",
    featuredTitle: "مجموعة مختارة من أدوات الذكاء الاصطناعي المجانية.",
    featuredDesc:
      "أدوات عملية ومجانية اخترناها بعناية لتكون مفيدة وسهلة الوصول في السياق السوري، سواء للتعلّم أو البحث أو الكتابة أو الإنتاج.",
    featured: [
      {
        slug: "chatgpt-free",
        tag: "محادثة عامة",
        name: "ChatGPT Free",
        desc: "مساعد ذكي للكتابة، الفهم، التلخيص، وتنظيم الأفكار.",
      },
      {
        slug: "google-gemini",
        tag: "محادثة عامة",
        name: "Google Gemini",
        desc: "مفيد في الشرح، العصف الذهني، والبحث السريع.",
      },
      {
        slug: "perplexity",
        tag: "بحث علمي",
        name: "Perplexity",
        desc: "أداة قوية للبحث السريع مع إجابات مدعومة بالمصادر.",
      },
    ],
    tryTool: "جرّب الأداة",
    viewAllTools: "عرض جميع الأدوات",

    // 4) Workshops & Programs
    workshopsEyebrow: "الورشات والبرامج",
    workshopsTitle: "ورشات وبرامج عملية داخل الجامعات والمراكز.",
    workshopsDesc:
      "نقدّم جلسات تعريفية، ورشات تطبيقية، ومسارات تدريبية قابلة للتنفيذ بحسب الجهة المستضيفة واحتياجات الجمهور.",
    wsFormats: [
      {
        tag: "جلسة تعريفية",
        title: "جلسة تعريفية واحدة",
        desc: "تقديم مبسّط وعملي يشرح أهم تطبيقات الذكاء الاصطناعي وكيفية الاستفادة منها.",
      },
      {
        tag: "ورشة تطبيقية",
        title: "ورشة تطبيقية عملية",
        desc: "جلسة تفاعلية تركّز على استخدام أدوات حقيقية في الدراسة والبحث والإنتاج.",
      },
      {
        tag: "مسار تدريبي",
        title: "مسار تدريبي متكامل",
        desc: "برنامج من عدّة جلسات يُبنى تدريجيًا ليمنح المشاركين فهمًا أعمق ومهارات عملية أوضح.",
      },
    ],
    browseWorkshops: "تصفّح الورشات",
    requestWorkshop: "اطلب استضافة ورشة",

    // 5) Impact
    impactEyebrow: "الأثر",
    impactTitle: "منذ الانطلاق، أثر ملموس ونمو متواصل.",
    impactDesc:
      "بدأت SAS بخطوات تطوعية بسيطة، ثم تحوّلت إلى نشاط عملي متزايد داخل الجامعات وبعض المساحات المجتمعية، مع تفاعل متنامٍ من الطلاب والمهتمين.",
    stats: [
      { v: "+10", l: "أنشطة وورشات" },
      { v: "+1000", l: "مشارك ومشاركة" },
      { v: "+460", l: "ضمن السلسلة الجامعية الرئيسية" },
      { v: "95%", l: "معدّل الرضا العام" },
    ],
    impactCta: "تعرّف على الأثر",

    // 6) Articles
    articlesEyebrow: "المقالات",
    articlesTitle: "محتوى عربي مبسّط عن الذكاء الاصطناعي في التعليم والبحث.",
    articlesDesc:
      "مقالات مختارة تساعد الطلاب والباحثين والمهتمّين على فهم الذكاء الاصطناعي وتطبيقاته بلغة واضحة وسياق مناسب.",
    viewAllArticles: "عرض كل المقالات",
    articlesSoon: "قريبًا",
    articlesSoonBody:
      "نعمل على إعداد سلسلة من المقالات العربية العملية حول الذكاء الاصطناعي وتطبيقاته.",

    // 7) Institutional CTA
    ctaTitle: "هل تمثّل جامعة أو مدرسة أو منظمة أو مركزًا تدريبيًا؟",
    ctaDesc:
      "يسعدنا التعاون معكم لتنظيم ورشة تعريفية أو تطبيقية في مجال الذكاء الاصطناعي بما يناسب جمهوركم واحتياجاتكم.",
    ctaBtn: "تواصل معنا",
  },

  en: {
    // 1) Hero
    eyebrow: "SAS · Syrian AI Society",
    title: "Syrian AI Society",
    lead:
      "A non-profit volunteer initiative advancing practical AI awareness in Syria through free tools, simplified Arabic content, and hands-on educational workshops for students, researchers, and the wider community.",
    ctaTools: "Explore Tools",
    ctaWorkshops: "Join Workshops",
    ctaCommunity: "Join the Community",
    ctaCommunityDesc: "Connect with students and learn AI together",
    communityCount: "Join 100+ students learning AI together",

    // 2) Vision 2026
    visionEyebrow: "Our Vision 2026",
    visionTitle: "An integrated Arabic learning path for AI.",
    visionBody:
      "We aim to expand the program across universities, schools, and organisations, and build an integrated Arabic learning path that connects awareness, tools, and real-world practice — helping SAS grow into a community-driven educational platform for AI in Syria.",
    visionCta: "Read the vision",

    // 3) Featured tools
    featuredEyebrow: "Featured tools",
    featuredTitle: "A curated selection of free AI tools.",
    featuredDesc:
      "Practical and accessible tools selected to support learning, research, writing, and everyday productivity in the Syrian context.",
    featured: [
      {
        slug: "chatgpt-free",
        tag: "General assistants",
        name: "ChatGPT Free",
        desc: "A versatile assistant for writing, understanding, summarising, and organising ideas.",
      },
      {
        slug: "google-gemini",
        tag: "General assistants",
        name: "Google Gemini",
        desc: "Useful for explanation, brainstorming, and fast exploration.",
      },
      {
        slug: "perplexity",
        tag: "Research",
        name: "Perplexity",
        desc: "A strong research assistant for quick answers backed by cited sources.",
      },
    ],
    tryTool: "Try tool",
    viewAllTools: "View all tools",

    // 4) Workshops & Programs
    workshopsEyebrow: "Workshops & programs",
    workshopsTitle: "Practical workshops and training formats for universities and centres.",
    workshopsDesc:
      "We offer introductory sessions, hands-on workshops, and structured learning tracks that adapt to the host institution and audience.",
    wsFormats: [
      {
        tag: "Introductory",
        title: "Introductory Session",
        desc: "A clear and practical introduction to AI tools and how they can support study, work, and daily life.",
      },
      {
        tag: "Hands-on",
        title: "Hands-on Workshop",
        desc: "An interactive session focused on applying real tools in research, learning, writing, and productivity.",
      },
      {
        tag: "Training track",
        title: "Structured Training Track",
        desc: "A multi-session format designed to build deeper understanding and stronger practical skills over time.",
      },
    ],
    browseWorkshops: "Browse Workshops",
    requestWorkshop: "Request a Workshop",

    // 5) Impact
    impactEyebrow: "Impact",
    impactTitle: "Growing impact since launch.",
    impactDesc:
      "SAS began as a volunteer effort and has grown into a practical educational presence across university and community spaces, with increasing engagement from students and learners.",
    stats: [
      { v: "10+", l: "Activities and workshops" },
      { v: "1000+", l: "Participants reached" },
      { v: "460+", l: "In the main university lecture series" },
      { v: "95%", l: "Overall satisfaction rate" },
    ],
    impactCta: "See the impact",

    // 6) Articles
    articlesEyebrow: "Articles",
    articlesTitle: "Simplified AI content for learning and research.",
    articlesDesc:
      "Selected articles that help students, researchers, and curious learners understand AI concepts and practical use cases — in clear, accessible language.",
    viewAllArticles: "View all articles",
    articlesSoon: "Coming soon",
    articlesSoonBody:
      "We are preparing a series of practical Arabic-first articles on AI and its applications.",

    // 7) Institutional CTA
    ctaTitle: "Do you represent a university, school, organisation, or training centre?",
    ctaDesc:
      "We would be glad to collaborate on an awareness lecture or a practical AI workshop tailored to your institution and audience.",
    ctaBtn: "Get in touch",
  },
};

/* ------------------------------------------------------------------ */
/*  Local presentational helpers                                       */
/* ------------------------------------------------------------------ */

/**
 * Centered title block reused at the top of every non-hero section.
 *
 * New heading hierarchy (consistent across Vision, Featured Tools,
 * Workshops, Impact, Articles):
 *
 *   Main title          — LARGE, PURPLE         (was the eyebrow)
 *   Supporting heading  — smaller, BLACK        (was the large title)
 *   Description         — body grey paragraph
 *
 * The thin gradient accent line stays anchored to the main (purple)
 * title because that is now the dominant element in the block.
 *
 * Both fields are optional individually but at least the `eyebrow`
 * (which now plays the role of the main title) must be supplied.
 */
function CenteredHeading({ eyebrow, title, description }) {
  return (
    <div className="max-w-3xl mx-auto text-center">
      {/* MAIN title — large + purple. Drives the section. */}
      <h2
        className="sas-section-title sas-heading-accent"
        data-testid="section-title"
      >
        {eyebrow}
      </h2>

      {/* Supporting heading — smaller, black, sits directly under the
          purple title. Kept as an <h3> so the document outline stays
          honest. Optional: not every card has a second-line message. */}
      {title && (
        <h3
          className="
            mt-5
            text-[color:var(--sas-ink)] font-semibold
            text-[1.25rem] sm:text-[1.45rem] md:text-[1.6rem]
            leading-[1.35] tracking-[-0.01em]
            max-w-2xl mx-auto
          "
          data-testid="section-subtitle"
        >
          {title}
        </h3>
      )}

      {description && (
        <p
          className="sas-body mt-6 text-[16px] leading-[1.85] max-w-2xl mx-auto"
          data-testid="section-description"
        >
          {description}
        </p>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */

export default function Home() {
  const { lang } = useLanguage();
  const c = C[lang];
  const { ARTICLES } = useArticles();
  // `getToolBySlug` lets the homepage Featured Tools section pull each
  // tool's external URL straight from the live admin catalog instead of
  // duplicating it in the static `featured[]` config below. This means a
  // tool URL update in /admin/tools immediately reflects on the homepage.
  const { getToolBySlug } = useTools();

  return (
    <>
      {/* ============================================================ */}
      {/*  1) HERO  — centered, single column, no eyebrow, no chips     */}
      {/* ============================================================ */}
      <section
        className="pt-10 md:pt-14 pb-[clamp(3.5rem,6vw,6.5rem)]"
        data-testid="home-hero"
      >
        <div className="sas-container">
          <div className="max-w-3xl mx-auto text-center">
            {/* Real, text-based <h1> for SEO crawlers and assistive tech.
                Visually hidden via the standard `sr-only` utility so the
                visible logo below stays the design source of truth.
                This is the document's single <h1>. */}
            <h1 className="sr-only" data-testid="hero-title-text">
              {c.title}
            </h1>

            {/* Visible brand mark — unchanged styling.
                The image keeps its `alt` for SEO crawlers that don't
                respect `aria-hidden`, but is marked `aria-hidden` so
                screen-reader users hear the localised title exactly
                once (announced by the <h1> above) instead of twice.
                The wrapper is a <div> now: there must be only one
                <h1> per page. */}
            <div
              className="m-0 leading-none flex justify-center"
              data-testid="hero-title"
            >
              <img
                src="/sas-hero-logo.svg"
                alt={c.title}
                aria-hidden="true"
                width="468"
                height="99"
                loading="eager"
                decoding="async"
                fetchPriority="high"
                className="
                  block w-full h-auto select-none
                  max-w-[18rem] sm:max-w-[22rem] md:max-w-[26rem] lg:max-w-[30rem]
                "
                data-testid="hero-logo"
              />
            </div>
            <p
              className="sas-lead mt-14 md:mt-16 mx-auto max-w-2xl"
              data-testid="hero-lead"
            >
              {c.lead}
            </p>
            {/* Primary CTAs — equal width on every viewport, generous gap.
                The wrapper stacks on the smallest screens so neither button
                gets clipped, then sits inline once there's room. */}
            <div
              className="
                mt-14 md:mt-16
                flex flex-col sm:flex-row items-stretch sm:items-center
                justify-center
                gap-4 sm:gap-6
                max-w-md mx-auto sm:max-w-none
              "
              data-testid="hero-actions"
            >
              <LocalizedLink
                to="/tools"
                className="sas-btn sas-btn-primary w-full sm:w-auto sm:min-w-[200px] justify-center"
                data-testid="hero-cta-tools"
              >
                {c.ctaTools}
              </LocalizedLink>
              <LocalizedLink
                to="/workshops"
                className="sas-btn sas-btn-ghost w-full sm:w-auto sm:min-w-[200px] justify-center"
                data-testid="hero-cta-workshops"
              >
                {c.ctaWorkshops}
              </LocalizedLink>
            </div>

            {/* Tertiary "Join the Community" action.
                Subtle, text-led, opens a small popover with the three
                channels (WhatsApp group / channel / email) so we don't
                push more chrome into the hero. */}
            <div
              className="mt-6 flex flex-col items-center"
              data-testid="hero-community"
            >
              <Popover>
                <PopoverTrigger asChild>
                  <button
                    type="button"
                    className="
                      group inline-flex items-center gap-2
                      text-[14px] font-medium
                      text-[color:var(--sas-accent)]
                      hover:text-[color:var(--sas-ink)]
                      transition-colors
                      focus-visible:outline-none focus-visible:ring-2
                      focus-visible:ring-[color:var(--sas-accent)]
                      focus-visible:ring-offset-2
                      rounded-full px-3 py-1.5
                    "
                    aria-haspopup="dialog"
                    data-testid="hero-cta-community"
                  >
                    <Users
                      size={15}
                      aria-hidden
                      className="opacity-90 group-hover:opacity-100 transition-opacity"
                    />
                    <span>{c.ctaCommunity}</span>
                  </button>
                </PopoverTrigger>
                <PopoverContent
                  align="center"
                  sideOffset={10}
                  className="w-[300px] sm:w-[340px] p-2"
                  data-testid="hero-community-popover"
                >
                  <div className="px-3 pt-2 pb-1">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-[color:var(--sas-ink-2)]">
                      {c.ctaCommunity}
                    </div>
                    <div className="text-[12.5px] text-[color:var(--sas-ink-2)] mt-0.5">
                      {c.ctaCommunityDesc}
                    </div>
                  </div>
                  <div className="mt-1">
                    <CommunityActions
                      lang={lang}
                      variant="popover"
                      testId="hero-community-actions"
                    />
                  </div>
                </PopoverContent>
              </Popover>

              {/* Optional small social-proof line. Sits under the
                  community trigger so it reinforces the next click. */}
              <p
                className="mt-3 text-[12px] text-[color:var(--sas-ink-2)] max-w-md text-center"
                data-testid="hero-community-count"
              >
                {c.communityCount}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  2) VISION 2026  — short, strategic, single column            */}
      {/*  Wrapped in a soft glass card so it visually anchors the      */}
      {/*  page without using a hard divider line.                      */}
      {/* ============================================================ */}
      <section className="sas-section pt-2" data-testid="home-vision">
        <div className="sas-container">
          <div className="sas-glass-card max-w-3xl mx-auto px-6 sm:px-10 py-12 sm:py-14 text-center">
            {/* MAIN title — large + purple. Drives the section. */}
            <h2
              className="sas-section-title sas-heading-accent"
              data-testid="vision-title"
            >
              {c.visionEyebrow}
            </h2>
            {/* Supporting heading — smaller, black, sits directly under
                the purple title. */}
            <h3
              className="
                mt-5
                text-[color:var(--sas-ink)] font-semibold
                text-[1.25rem] sm:text-[1.45rem] md:text-[1.6rem]
                leading-[1.35] tracking-[-0.01em]
                max-w-2xl mx-auto
              "
              data-testid="vision-eyebrow"
            >
              {c.visionTitle}
            </h3>
            <p
              className="sas-body mt-7 text-[17px] leading-[2]"
              data-testid="vision-body"
            >
              {c.visionBody}
            </p>
            <div className="mt-8">
              <LocalizedLink
                to="/about"
                className="sas-btn-link"
                data-testid="vision-cta"
              >
                {c.visionCta} <NextArrow size={16} />
              </LocalizedLink>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  3) FEATURED TOOLS  — exactly 3 cards + View all              */}
      {/* ============================================================ */}
      <section className="sas-section" data-testid="home-featured">
        <div className="sas-container">
          <CenteredHeading
            eyebrow={c.featuredEyebrow}
            title={c.featuredTitle}
            description={c.featuredDesc}
          />
          <div className="sas-grid-3 mt-12">
            {c.featured.map((t, idx) => {
              // Subtly highlight the first featured tool with the glass
              // surface. One spotlight card per section is enough — more
              // would cross from "premium accent" into "decorative noise".
              const glass = idx === 0;
              // Resolve the live tool record so the CTA opens the actual
              // external URL. There is intentionally no `/tools/<slug>`
              // detail route yet — clicking the CTA opens the tool in a
              // new tab. If the slug is missing from the admin catalog
              // (e.g. just removed), gracefully fall back to the Tools
              // page anchor so the click never 404s.
              const liveTool = getToolBySlug ? getToolBySlug(t.slug) : null;
              const externalUrl = liveTool && liveTool.url ? liveTool.url : null;
              return (
                <div
                  key={t.slug}
                  className={`relative flex flex-col ${
                    glass
                      ? "sas-glass-card p-7 lg:p-8"
                      : "sas-card"
                  }`}
                  data-testid={`featured-tool-${t.slug}`}
                  data-spotlight={glass ? "true" : undefined}
                >
                  <div className="absolute top-4 end-4 z-10">
                    <SaveButton
                      itemType="tool"
                      itemSlug={t.slug}
                      itemId={liveTool ? liveTool.id : undefined}
                      variant="card"
                      testId={`featured-tool-save-${t.slug}`}
                      stopPropagation={false}
                    />
                  </div>
                  <span className="sas-chip mb-4">{t.tag}</span>
                  <h3 className="sas-h3">{t.name}</h3>
                  <p className="sas-body mt-3 text-[15px] leading-[1.85] flex-1">
                    {t.desc}
                  </p>
                  {externalUrl ? (
                    <a
                      href={externalUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="sas-btn-link mt-6 self-start"
                      data-testid={`featured-tool-${t.slug}-cta`}
                    >
                      {c.tryTool} <NextArrow size={16} />
                    </a>
                  ) : (
                    <LocalizedLink
                      to={`/tools#tool-${t.slug}`}
                      className="sas-btn-link mt-6 self-start"
                      data-testid={`featured-tool-${t.slug}-cta`}
                    >
                      {c.tryTool} <NextArrow size={16} />
                    </LocalizedLink>
                  )}
                </div>
              );
            })}
          </div>
          <div className="mt-12 flex justify-center">
            <LocalizedLink
              to="/tools"
              className="sas-btn sas-btn-ghost"
              data-testid="featured-view-all"
            >
              {c.viewAllTools}
            </LocalizedLink>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  4) WORKSHOPS & PROGRAMS  — 3 formats + 2 CTAs                */}
      {/* ============================================================ */}
      <section className="sas-section" data-testid="home-workshops">
        <div className="sas-container">
          <CenteredHeading
            eyebrow={c.workshopsEyebrow}
            title={c.workshopsTitle}
            description={c.workshopsDesc}
          />
          <div className="sas-grid-3 mt-12">
            {c.wsFormats.map((f) => (
              <div
                key={f.title}
                className="sas-card"
                data-testid={`workshop-format-${f.tag}`}
              >
                <span className="sas-chip mb-4">{f.tag}</span>
                <h3 className="sas-h3">{f.title}</h3>
                <p className="sas-body mt-3 text-[15px] leading-[1.85]">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
          <div
            className="mt-12 flex flex-wrap items-center justify-center gap-3"
            data-testid="workshops-actions"
          >
            <LocalizedLink
              to="/workshops"
              className="sas-btn sas-btn-primary"
              data-testid="workshops-cta-browse"
            >
              {c.browseWorkshops}
            </LocalizedLink>
            <LocalizedLink
              to="/contact/host-workshop"
              className="sas-btn sas-btn-ghost"
              data-testid="workshops-cta-request"
            >
              {c.requestWorkshop}
            </LocalizedLink>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  5) IMPACT  — 4 metrics in a clean card grid                  */}
      {/* ============================================================ */}
      <section className="sas-section" data-testid="home-impact">
        <div className="sas-container">
          <CenteredHeading
            eyebrow={c.impactEyebrow}
            title={c.impactTitle}
            description={c.impactDesc}
          />
          <div
            className="
              mt-12 grid grid-cols-2 lg:grid-cols-4
              border border-[color:var(--sas-line)] rounded-2xl
              divide-y lg:divide-y-0 lg:divide-x
              divide-[color:var(--sas-line)]
              overflow-hidden bg-white
            "
            data-testid="impact-grid"
          >
            {c.stats.map((s, i) => (
              <div
                key={s.l}
                className={`
                  px-6 py-10 text-center
                  ${i % 2 === 1 ? "border-s lg:border-s-0" : ""}
                `}
                style={{ borderColor: "var(--sas-line)" }}
                data-testid={`impact-stat-${i}`}
              >
                <div
                  className="text-[44px] md:text-[52px] font-semibold tracking-tight leading-none"
                  style={{ color: "var(--sas-accent)" }}
                >
                  {s.v}
                </div>
                <div className="mt-4 text-sm text-[color:var(--sas-ink-2)] leading-relaxed">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-12 flex justify-center">
            <LocalizedLink
              to="/impact"
              className="sas-btn-link"
              data-testid="impact-cta"
            >
              {c.impactCta} <NextArrow size={16} />
            </LocalizedLink>
          </div>
        </div>
      </section>

      {/* ============================================================ */}
      {/*  6) ARTICLES  — exactly 2 featured + View all                 */}
      {/* ============================================================ */}
      <section className="sas-section" data-testid="home-articles">
        <div className="sas-container">
          <CenteredHeading
            eyebrow={c.articlesEyebrow}
            title={c.articlesTitle}
            description={c.articlesDesc}
          />
          {ARTICLES.length > 0 ? (
            <div
              className="grid grid-cols-1 md:grid-cols-2 gap-y-12 gap-x-8 mt-12 max-w-5xl mx-auto"
              data-testid="articles-grid"
            >
              {ARTICLES.slice(0, 2).map((a) => (
                <ArticleCard key={a.slug} article={a} compact />
              ))}
            </div>
          ) : (
            <div
              className="mt-12 border border-dashed border-[color:var(--sas-line)] rounded-xl py-12 px-6 text-center max-w-2xl mx-auto"
              data-testid="articles-empty"
            >
              <div className="sas-eyebrow mb-3">{c.articlesSoon}</div>
              <p className="sas-body">{c.articlesSoonBody}</p>
            </div>
          )}
          {ARTICLES.length > 0 && (
            <div className="mt-12 flex justify-center">
              <LocalizedLink
                to="/articles"
                className="sas-btn sas-btn-ghost"
                data-testid="articles-view-all"
              >
                {c.viewAllArticles}
              </LocalizedLink>
            </div>
          )}
        </div>
      </section>

      {/* ============================================================ */}
      {/*  7) INSTITUTIONAL CTA  — centered, single button              */}
      {/* ============================================================ */}
      <section
        className="sas-section-sm bg-[color:var(--sas-accent-soft)] border-y border-[color:var(--sas-line)]"
        data-testid="home-cta"
      >
        <div className="sas-container">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="sas-h2" data-testid="institutional-cta-title">
              {c.ctaTitle}
            </h3>
            <p
              className="sas-body mt-4 text-[16px] leading-[1.9]"
              data-testid="institutional-cta-body"
            >
              {c.ctaDesc}
            </p>
            <div className="mt-8 flex justify-center">
              <LocalizedLink
                to="/contact"
                className="sas-btn sas-btn-primary"
                data-testid="institutional-cta-button"
              >
                {c.ctaBtn}
              </LocalizedLink>
            </div>
          </div>
        </div>
      </section>

      {/* ================================================================ */}
      {/* Newsletter signup — last block before the footer per the brief.  */}
      {/* Source label `homepage` is recorded in the admin list so we can  */}
      {/* later see which surface drives most subscriptions.               */}
      {/* ================================================================ */}
      <NewsletterSignup source="homepage" testId="newsletter-signup-home" />
    </>
  );
}
